import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { ContactPointType } from "@/Common/Models/Contacts/Enums/ContactPointType";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { ContactPoint } from "./ContactPoint";

interface IAddressDisplayModel {
    streetAddress: string;
    city: string;
    zipCode: string;
    state: string;
    country: string;
}

class Address {
    public ContactPoint_id: string;
    public City: string;
    public ZipCode: string;
    public StreetAddress: string;
    public CustomCountryOrProvince: string;
    public CountryLookupId: string;
    public StateLookupId: string;
    public IsPreferred: boolean;

    public Id: string;

    // for edition
    public IsValid: boolean = false;
    public ContactPointTypeLookupId: string;// only when Loading from PhoneNumberLight
    public AvailableContactPointTypeLookupIds: FlatLookupView[];
    public AvailableCountryLookupIds: FlatLookupView[];
    public AvailableCountryRegionLookupIds: FlatLookupView[];

    constructor(address: Partial<Address> = {}) {
        this.ContactPoint_id = "";
        this.City = "";
        this.ZipCode = "";
        this.StreetAddress = "";
        this.CustomCountryOrProvince = "";
        this.StateLookupId = GuidHelper.Empty();
        this.IsPreferred = false;
        this.IsValid = true;

        Object.assign(this, address);
    }

    public static newAddress(contactPointType: ContactPointType): Address {
        let address = new Address();

        address.IsValid = true;
        address.AvailableContactPointTypeLookupIds = cacheStore.GetContactPointTypeLookups();
        address.ContactPointTypeLookupId = cacheStore.getLookupByContactPointType(contactPointType).LookupID;
        address.AvailableCountryLookupIds = cacheStore.GetCountryLookups();
        address.CountryLookupId = cacheStore.GetDefaultCountryForActiveSite().LookupID;
        address.AvailableCountryRegionLookupIds = cacheStore.GetCountryRegionLookups(cacheStore.GetDefaultCountryForActiveSite());
        address.StateLookupId = cacheStore.GetDefaultRegionForActiveSite().LookupID;
        address.ZipCode = "";
        address.City = "";
        address.StreetAddress = "";
        address.Id = sessionStore.newId().toString();
        address.ContactPoint_id = GuidHelper.Empty();

        return address;
    }

    public static IsEmpty(address: Address): boolean {
        return !address.City && !address.ZipCode && !address.StreetAddress;
    }

    public static GetFullAddress(address: Address): string {
        if (!address)
            return "";

        let fullAddress = address.StreetAddress;
        if (address.City?.trim())
            fullAddress += `, ${address.City}`;

        let province = cacheStore.GetLocalizedValueByLookupId(address.StateLookupId);
        if (province?.trim())
            fullAddress += `, ${province}`;

        if (address.ZipCode?.trim())
            fullAddress += `, ${address.ZipCode}`;

        let country = cacheStore.GetLocalizedValueByLookupId(address.CountryLookupId);
        if (country?.trim())
            fullAddress += `, ${country}`;

        return fullAddress;
    }

    public static GetAddressDisplayModel(address: Address): IAddressDisplayModel {
        let state = cacheStore.GetLocalizedValueByLookupId(address.StateLookupId);

        let country = address?.CountryLookupId
            ? cacheStore.GetLocalizedValueByLookupId(address.CountryLookupId)
            : address?.CustomCountryOrProvince ?? "";

        return {
            streetAddress: address?.StreetAddress?.trim() ?? "",
            city: address?.City?.trim() ?? "",
            zipCode: address?.ZipCode?.trim() ?? "",
            country: country,
            state: state
        };
    }

    public static GetCountryLookupId(addresses: Address[]): string {
        var countryLookupId = null;
        if (addresses != null && addresses.length > 0) {
            let preferredAddresses = addresses.filter(x => x.IsPreferred == true);

            if (preferredAddresses.length == 0)
                countryLookupId = addresses[0].CountryLookupId;
            else
                countryLookupId = preferredAddresses[0].CountryLookupId;
        }

        return countryLookupId;
    }

    public static GetStateLookupId(addresses: Address[]): string {
        var stateLookupId = null;
        if (addresses != null && addresses.length > 0) {
            let preferredAddresses = addresses.filter(x => x.IsPreferred == true);

            if (preferredAddresses.length == 0)
                stateLookupId = addresses[0].StateLookupId;
            else
                stateLookupId = preferredAddresses[0].StateLookupId;
        }

        return stateLookupId;
    }

    public static GetFormattedAddress(addresses: Address[], position: number = 0, IncludeCity: boolean = true): string {
        var addressText = "";
        const addressLengthBanner: number = 35;

        if (addresses != null && addresses.length > position) {
            if (addresses[position].StreetAddress && addresses[position].StreetAddress.length > 0) {
                addressText = addresses[position].StreetAddress.truncate(addressLengthBanner);
            }

            if (IncludeCity == true && addresses[position].City && addresses[position].City.length > 0) {
                const addressCity = addresses[position].City.truncate(addressLengthBanner);

                addressText += ", " + addressCity; // + " - " + cacheStore.GetLocalizedLookupValueById(self.addresses[0].StateLookupId);
            }

            if (addressText.length <= 0)
                addressText = "Ø " + cacheStore.GetString("AddressLabel");
        }

        return addressText;
    }

    public static GetFormattedCity(addresses: Address[], position: number = 0): string {
        if (addresses != null && addresses.length > position)
            return addresses[position].City;
        else
            return "";
    }

    public static GetFormattedZipCode(addresses: Address[], fallBackToNull: boolean = false): string {
        const zipCode = addresses?.[0]?.ZipCode;
        return zipCode
            ?? (fallBackToNull ? null : "Ø " + cacheStore.GetString("lblPostalCode"));
    }

    public static GetFormattedProvince(addresses: Address[], fallBackToNull: boolean = false): string {
        const lookupId = addresses?.[0]?.StateLookupId;
        return lookupId
            ? cacheStore.GetLocalizedValueByLookupId(lookupId)
            : (fallBackToNull ? null : "Ø " + cacheStore.GetString("StateDLabel"));
    }

    public static GetFormattedCountry(addresses: Address[]): string {
        var countryText = "";
        if (addresses != null && addresses.length > 0 && addresses[0].CountryLookupId)
            countryText = cacheStore.GetLocalizedValueByLookupId(addresses[0].CountryLookupId);

        return countryText;
    }

    public static GetFormattedAddressType(addresses: Address[], contactPoints: ContactPoint[], IncludeParentesis: boolean = false): string {
        if (addresses != null && addresses.length > 0 && addresses[0] != null) {
            var currentCP = new ContactPoint();
            var currentCPs = contactPoints.filter(x => x.Id == addresses[0].ContactPoint_id);
            if (currentCPs != null && currentCPs.length > 0) {
                currentCP = currentCPs[0];

                var result = cacheStore.GetLocalizedValueByLookupId(currentCP.ContactPointTypeLookupId);

                if (IncludeParentesis)
                    return "(" + result + ")";
                else
                    return result;
            }
        }

        return "";
    }

    public static getDefaultAddress(addresses: Address[]): Address {
        return addresses.firstOrDefault(address => address.IsPreferred) ?? addresses.firstOrDefault();
    }

    public static getDefaultAddressStateLookup(addresses: Address[]): FlatLookupView {
        const defaultAddress = this.getDefaultAddress(addresses);

        return cacheStore.GetLookupById(defaultAddress?.StateLookupId);
    }

    public static isDefaultAddressInCanada(addresses: Address[]): boolean {
        return Address.isAddressInCanada(this.getDefaultAddress(addresses));
    }

    public static isAddressInCanada(address: Address): boolean {
        return address?.CountryLookupId === cacheStore.FindLookupByLookupCode("CNCANADA").LookupID;
    }

    public static getRegionLookupCode(address: Address): string {
        return address
            ?.AvailableCountryRegionLookupIds
            ?.firstOrDefault(region => region.LookupID === address.StateLookupId)
            ?.LookupCode;
    }

    public static isAddressInProvince(address: Address, province: CanadaState): boolean {
        return address?.StateLookupId === cacheStore.GetLookupIdForProvince(province);
    }

    public static hasAddressInProvince(addresses: Address[], province: CanadaState): boolean {
        const provinceLookupId = cacheStore.GetLookupIdForProvince(province);
        return !!addresses?.any(p => p.StateLookupId === provinceLookupId);
    }
}

export { Address };
